import { SyntheticEvent, useEffect, useState } from 'react'
import { useReactiveVar } from '@apollo/client'
import CheckBoxIcon from '@mui/icons-material/CheckBox'
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank'
import {
  Autocomplete,
  AutocompleteRenderGroupParams,
  Box,
  Checkbox,
  Link,
  TextField,
  Typography
} from '@mui/material'
import { filteringOnlyByMealSubtypes } from 'components/Events/EventFilters/Filters/helpers'
import {
  eventFilterSubtypes,
  groupedEventFilterSubtypes,
  OrderByFilterOption,
  orderByFilterVar,
  typeFilterVar
} from 'context'
import { useSegmentInteraction } from 'hooks/analytics/segment'
import { lowerCase, uniq } from 'lodash-es'
import { getFilterPart } from './helpers/getFilterPart'
import type { Dictionary } from 'lodash'

enum FilterParts {
  'TYPE' = 0,
  'SUBTYPE' = 1
}

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />
const checkedIcon = <CheckBoxIcon fontSize="small" />

export const TypeFilter = () => {
  const { track } = useSegmentInteraction()
  const checkedSubtypes = useReactiveVar(typeFilterVar)
  const [sectionHeaderState, setSectionHeaderState] = useState<Dictionary<boolean>>({})

  const renderOption = (props: any, option: string, state: any) => {
    return (
      <div {...props} value={option}>
        <Checkbox
          icon={icon}
          checkedIcon={checkedIcon}
          sx={{
            marginRight: 1,
            marginLeft: 1
          }}
          checked={state.selected}
        />
        {getFilterPart(option, FilterParts.SUBTYPE)}
      </div>
    )
  }

  useEffect(() => {
    if (!filteringOnlyByMealSubtypes(checkedSubtypes)) {
      orderByFilterVar(OrderByFilterOption.Default)
    }
  }, [checkedSubtypes])

  const toggleSectionHeader = (section: keyof typeof groupedEventFilterSubtypes) => {
    const status = !sectionHeaderState[section]
    const nextState = { ...sectionHeaderState, [section]: status }

    let nextCheckedSubtypes: string[]

    if (status) {
      nextCheckedSubtypes = uniq([
        ...checkedSubtypes,
        ...groupedEventFilterSubtypes[section]
      ]).sort()
    } else {
      nextCheckedSubtypes = checkedSubtypes.filter(
        (subtype) => !subtype.includes(section) && subtype
      )
    }

    setSectionHeaderState(nextState)
    typeFilterVar(nextCheckedSubtypes)
  }

  const renderSection = ({ key, group: section, children }: AutocompleteRenderGroupParams) => {
    return (
      <Box component="div" key={`event-list-section-${key}`}>
        <Box
          component="div"
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            padding: 2,
            alignContent: 'center'
          }}
        >
          <Typography component="span" variant="h6">
            {section}
          </Typography>
          <Typography
            component="span"
            sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
          >
            <Link
              underline="hover"
              variant="body1"
              onClick={() =>
                toggleSectionHeader(lowerCase(section) as keyof typeof groupedEventFilterSubtypes)
              }
            >
              {sectionHeaderState[lowerCase(section)] ? 'Deselect All' : 'Select All'}
            </Link>
          </Typography>
        </Box>
        {children}
      </Box>
    )
  }

  const deselectIncompleteSections = (nextCheckedSubtypes: string[]) => {
    const incompleteSections = Object.keys(groupedEventFilterSubtypes).filter(
      (type: keyof typeof groupedEventFilterSubtypes) => {
        const activeTypeFilters = nextCheckedSubtypes.filter((subtype) =>
          subtype.includes(type)
        ).length

        return (
          activeTypeFilters > 0 && activeTypeFilters !== groupedEventFilterSubtypes[type].length
        )
      }
    )

    const nextState = { ...sectionHeaderState }
    incompleteSections.forEach((section) => (nextState[section] = false))

    setSectionHeaderState(nextState)
  }

  const handleFilterChange = (_ev: SyntheticEvent, nextCheckedSubtypes: string[]) => {
    track('Events "type" filter changed', { filterValue: nextCheckedSubtypes })
    deselectIncompleteSections(nextCheckedSubtypes)
    typeFilterVar(nextCheckedSubtypes)
  }

  return (
    <Autocomplete
      multiple
      size="small"
      id="eventFilters"
      limitTags={2}
      options={eventFilterSubtypes}
      disableCloseOnSelect
      renderOption={renderOption}
      renderGroup={renderSection}
      getOptionLabel={(option) => getFilterPart(option, FilterParts.SUBTYPE)}
      groupBy={(option) => getFilterPart(option, FilterParts.TYPE)}
      sx={{ width: '70%', marginRight: 1 }}
      value={checkedSubtypes}
      onChange={handleFilterChange}
      renderInput={(params) => <TextField {...params} label="Filter Events" placeholder="Filter" />}
    />
  )
}
